import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Credential } from '../types';
import { Plus, Search, Eye, EyeOff, Copy, Edit, Trash2, Share2, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import CredentialModal from '../components/CredentialModal';
import ShareModal from '../components/ShareModal';
import './Credentials.css';

const CATEGORY_COLORS: Record<string, string> = {
  VIRTUAL_MACHINE: '#3b82f6',
  APPLICATION: '#8b5cf6',
  NETWORK_DEVICE: '#10b981',
  SERVER: '#f59e0b',
  CLOUD_SERVICE: '#06b6d4',
  DATABASE: '#ec4899',
  OTHER: '#64748b',
};

const Credentials: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [type, setType] = useState(searchParams.get('type') || '');
  const [showModal, setShowModal] = useState(searchParams.get('new') === 'true');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingCredential, setSharingCredential] = useState<Credential | null>(null);
  const [editingCredential, setEditingCredential] = useState<Credential | null>(null);
  const [revealedPasswords, setRevealedPasswords] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['credentials', search, category, type],
    async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (type) params.append('type', type);
      const response = await api.get(`/credentials?${params.toString()}`);
      return response.data;
    }
  );

  const deleteMutation = useMutation(
    async (id: string) => {
      await api.delete(`/credentials/${id}`);
    },
    {
      onSuccess: () => {
        toast.success('Credential deleted successfully');
        queryClient.invalidateQueries('credentials');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to delete credential');
      },
    }
  );

  const credentials: Credential[] = data?.credentials || [];

  const handleSearch = (value: string) => {
    setSearch(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
      // Auto-clear after 30 seconds (security best practice)
      setTimeout(async () => {
        await navigator.clipboard.writeText('');
      }, 30000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const toggleReveal = (id: string) => {
    setRevealedPasswords((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this credential?')) {
      deleteMutation.mutate(id);
    }
  };

  const categories: Credential['category'][] = [
    'VIRTUAL_MACHINE',
    'APPLICATION',
    'NETWORK_DEVICE',
    'SERVER',
    'CLOUD_SERVICE',
    'DATABASE',
    'OTHER',
  ];

  const types: Credential['type'][] = ['USERNAME_PASSWORD', 'SSH_KEY', 'API_TOKEN', 'CERTIFICATE'];

  return (
    <div className="credentials-page">
      <div className="page-header">
        <div>
          <h1>Credentials</h1>
          <p>Manage and access all IT credentials</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} />
          <span>Add Credential</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search credentials..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            const params = new URLSearchParams(searchParams);
            if (e.target.value) {
              params.set('category', e.target.value);
            } else {
              params.delete('category');
            }
            setSearchParams(params);
          }}
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            const params = new URLSearchParams(searchParams);
            if (e.target.value) {
              params.set('type', e.target.value);
            } else {
              params.delete('type');
            }
            setSearchParams(params);
          }}
        >
          <option value="">All Types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="credentials-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div className="credential-card" key={i}>
              <div className="skeleton" style={{ width: '60%', height: 22, marginBottom: 12 }} />
              <div className="skeleton" style={{ width: '40%', height: 14, marginBottom: 20 }} />
              <div className="skeleton" style={{ width: '100%', height: 80 }} />
            </div>
          ))}
        </div>
      ) : credentials.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><KeyRound size={28} /></div>
          <p>No credentials found</p>
          <span>Add your first credential to start building your vault</span>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={20} />
            <span>Add Your First Credential</span>
          </button>
        </div>
      ) : (
        <div className="credentials-grid">
          {credentials.map((credential) => {
            const categoryColor = CATEGORY_COLORS[credential.category] || CATEGORY_COLORS.OTHER;
            return (
            <div key={credential.id} className="credential-card" style={{ borderTopColor: categoryColor }}>
              <div className="credential-header">
                <div>
                  <h3>{credential.name}</h3>
                  <div className="credential-meta">
                    <span className="badge" style={{ backgroundColor: `${categoryColor}18`, color: categoryColor }}>
                      {credential.category.replace(/_/g, ' ')}
                    </span>
                    <span className="badge">{credential.type.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                <div className="credential-actions">
                  {credential.permissions.canView && (
                    <button
                      className="icon-btn"
                      onClick={() => {
                        setSharingCredential(credential);
                        setShowShareModal(true);
                      }}
                      title="Share credential"
                    >
                      <Share2 size={16} />
                    </button>
                  )}
                  {credential.permissions.canEdit && (
                    <button
                      className="icon-btn"
                      onClick={() => {
                        setEditingCredential(credential);
                        setShowModal(true);
                      }}
                      title="Edit credential"
                    >
                      <Edit size={16} />
                    </button>
                  )}
                  {credential.permissions.canDelete && (
                    <button
                      className="icon-btn"
                      onClick={() => handleDelete(credential.id)}
                      title="Delete credential"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              {credential.description && (
                <p className="credential-description">{credential.description}</p>
              )}

              {/* Show warning badge if credential is from deleted user */}
              {credential.tags && credential.tags.some((tag: string) => tag === 'deleted-user') && (
                <div className="deleted-user-badge">
                  Transferred from deleted user
                </div>
              )}

              {credential.data && credential.permissions.canView && (
                <div className="credential-data">
                  {credential.data.username && (
                    <div className="data-field">
                      <label>Username:</label>
                      <div className="field-value">
                        <span>{credential.data.username}</span>
                        <button
                          className="copy-btn"
                          onClick={() => handleCopy(credential.data!.username!, 'Username')}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                  {credential.data.password && (
                    <div className="data-field">
                      <label>Password:</label>
                      <div className="field-value">
                        <span>
                          {revealedPasswords.has(credential.id)
                            ? credential.data.password
                            : '••••••••'}
                        </span>
                        <div className="field-actions">
                          <button
                            className="copy-btn"
                            onClick={() => handleCopy(credential.data!.password!, 'Password')}
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            className="copy-btn"
                            onClick={() => toggleReveal(credential.id)}
                          >
                            {revealedPasswords.has(credential.id) ? (
                              <EyeOff size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {credential.data.host && (
                    <div className="data-field">
                      <label>Host:</label>
                      <div className="field-value">
                        <span>{credential.data.host}</span>
                        <button
                          className="copy-btn"
                          onClick={() => handleCopy(credential.data!.host!, 'Host')}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                  {credential.data.port && (
                    <div className="data-field">
                      <label>Port:</label>
                      <div className="field-value">
                        <span>{credential.data.port}</span>
                        <button
                          className="copy-btn"
                          onClick={() => handleCopy(String(credential.data!.port), 'Port')}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                  {credential.data.token && (
                    <div className="data-field">
                      <label>Token:</label>
                      <div className="field-value">
                        <span>
                          {revealedPasswords.has(credential.id) ? credential.data.token : '••••••••'}
                        </span>
                        <div className="field-actions">
                          <button
                            className="copy-btn"
                            onClick={() => handleCopy(credential.data!.token!, 'Token')}
                          >
                            <Copy size={14} />
                          </button>
                          <button className="copy-btn" onClick={() => toggleReveal(credential.id)}>
                            {revealedPasswords.has(credential.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {credential.tags && credential.tags.length > 0 && (
                <div className="credential-tags">
                  {credential.tags.map((tag, idx) => {
                    const isDeletedUserTag = tag === 'deleted-user' || tag.startsWith('transferred-from-');
                    return (
                      <span 
                        key={idx} 
                        className={`tag ${isDeletedUserTag ? (tag === 'deleted-user' ? 'deleted-user' : 'transferred-from') : ''}`}
                      >
                        {tag === 'deleted-user' ? '⚠️ Deleted User' : tag.startsWith('transferred-from-') ? `🔄 ${tag.replace('transferred-from-', 'From: ')}` : tag}
                    </span>
                    );
                  })}
                </div>
              )}

              <Link to={`/credentials/${credential.id}`} className="view-details-link">
                View Details →
              </Link>
            </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <CredentialModal
          credential={editingCredential}
          onClose={() => {
            setShowModal(false);
            setEditingCredential(null);
            const params = new URLSearchParams(searchParams);
            params.delete('new');
            setSearchParams(params);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingCredential(null);
            queryClient.invalidateQueries('credentials');
            const params = new URLSearchParams(searchParams);
            params.delete('new');
            setSearchParams(params);
          }}
        />
      )}

      {showShareModal && sharingCredential && (
        <ShareModal
          credentialId={sharingCredential.id}
          credentialName={sharingCredential.name}
          onClose={() => {
            setShowShareModal(false);
            setSharingCredential(null);
          }}
          onSuccess={() => {
            setShowShareModal(false);
            setSharingCredential(null);
            queryClient.invalidateQueries('credentials');
          }}
        />
      )}
    </div>
  );
};

export default Credentials;

